import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserPlus, UserMinus, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export const AdminMakeAffiliatePanel = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [removeEmail, setRemoveEmail] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeResult, setRemoveResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleMakeAffiliate = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);

    try {
      const { data: exists, error: checkError } = await supabase.rpc('check_user_exists_by_email', {
        user_email: email.trim(),
      });

      if (checkError) throw checkError;
      if (!exists) {
        setResult({ type: 'error', message: isRTL ? 'لم يتم العثور على مستخدم بهذا البريد الإلكتروني' : 'No user found with this email' });
        setLoading(false);
        return;
      }

      const { data: existingApp } = await supabase
        .from('affiliate_applications')
        .select('user_id, approval_status')
        .eq('email', email.trim())
        .maybeSingle();

      if (existingApp) {
        if (existingApp.approval_status === 'approved') {
          setResult({ type: 'error', message: isRTL ? 'هذا المستخدم بالفعل شريك إحالة معتمد' : 'This user is already an approved affiliate' });
          setLoading(false);
          return;
        }
        const { error: updateError } = await supabase
          .from('affiliate_applications')
          .update({ approval_status: 'approved', approved_at: new Date().toISOString() })
          .eq('user_id', existingApp.user_id);
        if (updateError) throw updateError;

        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: existingApp.user_id,
            username: email.split('@')[0],
            user_type: 'affiliate' as any,
          }, { onConflict: 'id' });
        if (profileError) throw profileError;

        setResult({ type: 'success', message: isRTL ? 'تمت الموافقة على الطلب الموجود وتحديث الملف الشخصي' : 'Existing application approved and profile updated' });
      } else {
        const { data: campaign } = await supabase
          .from('campaigns')
          .select('created_by')
          .eq('email', email.trim())
          .limit(1)
          .maybeSingle();

        let userId = campaign?.created_by;

        if (!userId) {
          const { data: saved } = await supabase
            .from('saved_campaigns')
            .select('user_id')
            .eq('email', email.trim())
            .limit(1)
            .maybeSingle();
          userId = saved?.user_id;
        }

        if (!userId) {
          setResult({
            type: 'error',
            message: isRTL
              ? 'تم العثور على المستخدم ولكن لم نتمكن من تحديد معرفه. اطلب منه التقدم بطلب أولاً.'
              : 'User exists but we could not resolve their ID. Ask them to submit an application first.',
          });
          setLoading(false);
          return;
        }

        const { error: insertError } = await supabase
          .from('affiliate_applications')
          .insert({
            user_id: userId,
            applicant_name: email.split('@')[0],
            email: email.trim(),
            applicant_type: 'individual',
            approval_status: 'approved',
            approved_at: new Date().toISOString(),
          });
        if (insertError) throw insertError;

        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            username: email.split('@')[0],
            user_type: 'affiliate' as any,
          }, { onConflict: 'id' });
        if (profileError) throw profileError;

        setResult({ type: 'success', message: isRTL ? 'تم تحويل المستخدم إلى شريك إحالة بنجاح' : 'User has been made an affiliate successfully' });
      }

      setEmail("");
    } catch (err: any) {
      console.error('Error making affiliate:', err);
      setResult({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAffiliate = async () => {
    if (!removeEmail.trim()) return;
    setRemoveLoading(true);
    setRemoveResult(null);

    try {
      const { data: app, error: appError } = await supabase
        .from('affiliate_applications')
        .select('user_id, approval_status')
        .eq('email', removeEmail.trim())
        .maybeSingle();

      if (appError) throw appError;

      if (!app) {
        setRemoveResult({ type: 'error', message: isRTL ? 'لم يتم العثور على طلب شريك إحالة بهذا البريد الإلكتروني' : 'No affiliate application found with this email' });
        setRemoveLoading(false);
        return;
      }

      if (app.approval_status === 'revoked') {
        setRemoveResult({ type: 'error', message: isRTL ? 'تم إلغاء صلاحية هذا الشريك مسبقاً' : 'This affiliate has already been revoked' });
        setRemoveLoading(false);
        return;
      }

      const { error: updateAppError } = await supabase
        .from('affiliate_applications')
        .update({ approval_status: 'revoked' })
        .eq('user_id', app.user_id);
      if (updateAppError) throw updateAppError;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ user_type: 'media_owner' as any })
        .eq('id', app.user_id);
      if (profileError) throw profileError;

      setRemoveResult({ type: 'success', message: isRTL ? 'تم إلغاء صلاحية شريك الإحالة بنجاح' : 'Affiliate status has been revoked successfully' });
      setRemoveEmail("");
    } catch (err: any) {
      console.error('Error removing affiliate:', err);
      setRemoveResult({ type: 'error', message: err.message });
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {isRTL ? "تحويل مستخدم إلى شريك إحالة" : "Make User an Affiliate"}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? "أدخل البريد الإلكتروني للمستخدم لتحويله إلى شريك إحالة معتمد"
              : "Enter a user's email to convert them into an approved affiliate partner"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-md">
            <div>
              <Label>{isRTL ? "البريد الإلكتروني للمستخدم" : "User Email"}</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleMakeAffiliate()}
              />
            </div>
            <Button onClick={handleMakeAffiliate} disabled={loading || !email.trim()}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {isRTL ? "تحويل إلى شريك" : "Make Affiliate"}
            </Button>
            {result && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                result.type === 'success' ? 'bg-green-500/10 text-green-700' : 'bg-destructive/10 text-destructive'
              }`}>
                {result.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
                {result.message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserMinus className="h-5 w-5 text-destructive" />
            {isRTL ? "إلغاء صلاحية شريك إحالة" : "Remove Affiliate Status"}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? "أدخل البريد الإلكتروني لشريك الإحالة لإلغاء صلاحيته وإعادته كمستخدم عادي"
              : "Enter an affiliate's email to revoke their status and revert them to a regular user"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-md">
            <div>
              <Label>{isRTL ? "البريد الإلكتروني للشريك" : "Affiliate Email"}</Label>
              <Input
                type="email"
                placeholder="affiliate@example.com"
                value={removeEmail}
                onChange={e => setRemoveEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRemoveAffiliate()}
              />
            </div>
            <Button variant="destructive" onClick={handleRemoveAffiliate} disabled={removeLoading || !removeEmail.trim()}>
              {removeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserMinus className="h-4 w-4 mr-2" />}
              {isRTL ? "إلغاء الصلاحية" : "Revoke Affiliate"}
            </Button>
            {removeResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                removeResult.type === 'success' ? 'bg-green-500/10 text-green-700' : 'bg-destructive/10 text-destructive'
              }`}>
                {removeResult.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
                {removeResult.message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};